/**
 * Bug 22504 — logos on designs saved before the builder's fixed artwork sizing.
 *
 * Until builder PR 16366 went live (first live build 2026-09-21 10:55 UTC) the
 * 3D editor drew artwork at `containerWidth / 600`, the width of the
 * customer's editor on screen, so a saved logo `scaleX` only meant something
 * with that width. Since then the builder and this engine read every saved
 * scale at the fixed 760/600: a logo saved in a 512 px editor (Hummel) draws
 * 760/512 = 1.48x too big. The logo's stored cm (`originalWidth`) is what the
 * customer saw and what the order PDF says, so for those designs each logo is
 * fitted back to it. Designs created after the release round-trip and are
 * left alone (logos edited on them already store their drawn size).
 */
import { PT_PER_CM } from './units';

export const ARTWORK_SIZING_FIX_LIVE_AT = Date.parse('2026-09-21T10:55:00Z');

/** True for a design first saved before the fix went live. Unknown date: false. */
export function savedBeforeArtworkSizingFix(createdAt?: string | null): boolean {
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  return Number.isFinite(t) && t < ARTWORK_SIZING_FIX_LIVE_AT;
}

/** Below this the logo already has its stored size; no rounding-level nudges. */
const TOLERANCE = 0.02;
/** A factor outside this range is bad data, not the editor-width drift. */
const MIN_FACTOR = 0.25;
const MAX_FACTOR = 4;

/**
 * Scale factor that brings a logo drawn `drawnUnits` wide (unrotated, canvas
 * units) back to its stored width, or null to leave it as drawn: no stored
 * width, already within tolerance, or an implausible factor.
 */
export function storedSizeFactor(
  storedWidthCm: unknown,
  drawnUnits: number,
  ptPerUnit: number
): number | null {
  const stored = Number(storedWidthCm);
  const drawnCm = (drawnUnits * ptPerUnit) / PT_PER_CM;
  if (!(stored > 0) || !(drawnCm > 0)) return null;
  const factor = stored / drawnCm;
  if (!Number.isFinite(factor) || factor < MIN_FACTOR || factor > MAX_FACTOR) return null;
  return Math.abs(factor - 1) <= TOLERANCE ? null : factor;
}
